import { Users, ShieldPlus, BadgeCheck, MessageCircleHeart } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, stagger, item } from "../lib/motion";

const FEATURES = [
  {
    icon: Users,
    title: "People Who Understand",
    body: "Our drivers have personal or professional knowledge of substance use disorder and recovery—and often both.",
  },
  {
    icon: ShieldPlus,
    title: "Prepared for Emergencies",
    body: "Drivers are trained and equipped with Narcan for immediate life-saving opioid-overdose emergencies.",
  },
  {
    icon: BadgeCheck,
    title: "Vetted Drivers",
    body: "Active drivers are background checked, and their driver's license, vehicle registration, and insurance are verified.",
  },
  {
    icon: MessageCircleHeart,
    title: "Communication That Supports Continuity of Care",
    body: "When appropriate, drivers can communicate relevant safety or sobriety concerns to the requesting provider so its staff can determine whether additional support, observation, or intervention may be needed.",
  },
];

export const WhyUs = () => (
  <section id="why" className="relative bg-white py-20 sm:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
      <Reveal className="max-w-3xl">
        <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-[#0891B2] mb-4">
          Why Recovery Rides
        </p>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#3B3A4E] leading-tight">
          A Different Kind of Ride
        </h2>
      </Reveal>

      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        className="mt-12 grid sm:grid-cols-2 gap-6 lg:gap-8"
      >
        {FEATURES.map((f, i) => (
          <motion.div
            key={f.title}
            variants={item}
            className="group relative rounded-3xl border border-[#3B3A4E]/8 bg-[#FAFAF9] p-8 sm:p-9 overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-[#3B3A4E]/10 hover:border-[#00B4B4]/30"
            data-testid={`why-card-${i}`}
          >
            <span className="absolute top-6 right-7 font-mono text-4xl font-bold text-[#3B3A4E]/8 group-hover:text-[#1AD1B9]/25 transition-colors">
              {String(i + 1).padStart(2, "0")}
            </span>
            <div className="h-13 w-13 rounded-2xl brand-gradient flex items-center justify-center mb-5 shadow-md shadow-teal-500/20" style={{ height: "3.25rem", width: "3.25rem" }}>
              <f.icon size={24} className="text-white" />
            </div>
            <h3 className="text-lg sm:text-xl font-heading font-semibold text-[#2D2B3D] leading-snug max-w-sm">
              {f.title}
            </h3>
            <p className="mt-3 text-sm sm:text-base text-[#5C5975] leading-relaxed">{f.body}</p>
          </motion.div>
        ))}
      </motion.div>

      <Reveal delay={0.1}>
        <p className="mt-8 text-sm text-[#8B88A5] italic max-w-3xl">
          Recovery Rides is a transportation and concierge service. We do not provide medical care, clinical
          assessments, patient monitoring, or emergency medical transportation.
        </p>
      </Reveal>
    </div>
  </section>
);
